// WikiBlockEditor.jsx — edit-mode block list: per-block grip/add/delete,
// native drag-reorder, and an add-block (slash-style) menu.
import React, { useState } from 'react';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import WikiBlock from './WikiBlock';
import { WIKI_BLOCK_MENU, createBlock } from './wikiBlockRegistry';

function AddMenu({ onPick, onClose }) {
    return (
        <>
            <div className="fixed inset-0 z-30" onClick={onClose} />
            <div className="absolute left-0 top-full mt-1 w-52 max-h-72 overflow-auto bg-white rounded-xl border border-stone-200 wiki-shadow-float z-40 p-1">
                {WIKI_BLOCK_MENU.map(item => (
                    <button key={item.type} onClick={() => { onPick(item.type); onClose(); }}
                        className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-stone-50 text-left text-[13px] text-stone-700">
                        <item.icon className="w-4 h-4 text-stone-500" /> {item.label}
                    </button>
                ))}
            </div>
        </>
    );
}

export default function WikiBlockEditor({ blocks, onChange }) {
    const [drag, setDrag] = useState(null);     // index
    const [over, setOver] = useState(null);
    const [menuAt, setMenuAt] = useState(null); // index after which to insert, or 'end'

    const updateBlock = (blockId, newProps) =>
        onChange(blocks.map(b => b.block_id === blockId ? { ...b, props: newProps } : b));

    const insertAfter = (index, type) => {
        const nb = createBlock(type);
        const next = [...blocks];
        next.splice(index + 1, 0, nb);
        onChange(next);
    };
    const deleteBlock = (index) => onChange(blocks.filter((_, i) => i !== index));

    const move = (from, to) => {
        if (from === to || from < 0 || to < 0) return;
        const next = [...blocks];
        const [m] = next.splice(from, 1);
        next.splice(to, 0, m);
        onChange(next);
    };

    return (
        <div>
            <div className="mb-3 text-[12px] text-stone-400 flex items-center gap-1.5 border border-dashed border-stone-200 rounded-lg px-3 py-2">
                Drag <GripVertical className="w-3 h-3 inline" /> to reorder · hover a block for <Plus className="w-3 h-3 inline" /> to add below
            </div>
            {blocks.map((b, i) => (
                <div key={b.block_id} className="group relative flex items-start gap-1"
                    onDragOver={(e) => { if (drag != null) { e.preventDefault(); setOver(i); } }}
                    onDrop={(e) => { e.preventDefault(); if (drag != null) move(drag, i); setDrag(null); setOver(null); }}>
                    <div className="flex flex-col items-center pt-2 opacity-0 group-hover:opacity-100 transition shrink-0 w-6">
                        <button draggable onDragStart={() => setDrag(i)} onDragEnd={() => { setDrag(null); setOver(null); }}
                            className="text-stone-300 hover:text-stone-500 cursor-grab" title="Drag to reorder"><GripVertical className="w-4 h-4" /></button>
                        <div className="relative">
                            <button onClick={() => setMenuAt(menuAt === i ? null : i)} className="text-stone-300 hover:text-stone-500" title="Add block below"><Plus className="w-4 h-4" /></button>
                            {menuAt === i && <AddMenu onPick={(t) => insertAfter(i, t)} onClose={() => setMenuAt(null)} />}
                        </div>
                        <button onClick={() => deleteBlock(i)} className="text-stone-300 hover:text-rose-500" title="Delete block"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                    <div className={`flex-1 min-w-0 rounded ${over === i ? 'ring-1 ring-stone-400' : ''}`}>
                        <WikiBlock b={b} editing onChange={updateBlock} />
                    </div>
                </div>
            ))}
            <div className="relative mt-4 ml-7">
                <button onClick={() => setMenuAt(menuAt === 'end' ? null : 'end')}
                    className="text-[12px] font-semibold text-stone-400 hover:text-stone-700 inline-flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add block</button>
                {menuAt === 'end' && <AddMenu onPick={(t) => insertAfter(blocks.length - 1, t)} onClose={() => setMenuAt(null)} />}
            </div>
        </div>
    );
}
